import type { PolygonCoordinates, RectCoordinates, RectSize } from '@ricoh/ria.client.core/types/component/layout.type';
import type { ShapeConfig } from 'konva/lib/Shape';
import type { RectConfig } from 'konva/lib/shapes/Rect';

import { ClientError } from '@ricoh/ria.client.core/errors';

import type { OCRPositionBox, OCRPositionPolygon } from '~/common/model/api/batch';

import type { CreateOCRShapesOptions, OCRShape, OCRShapeLine, OCRShapeRect, OCRShapesIterator } from './_shape.type';

import { isPosition, rotateOCRPosition } from './_position';

/** Create Shape Id. */
export const createShapeId = {
  box: ({ x1, x2, y1, y2 }: RectCoordinates) => `[${x1};${y1}][${x2};${y2}]`,
  polygon: ([x1, y1, x2, y2, x3, y3, x4, y4]: PolygonCoordinates) => `[${x1};${y1}][${x2};${y2}][${x3};${y3}][${x4};${y4}]`,
};

export const createOCRShapeConfig = {
  box: (box: OCRPositionBox, ratio: RectSize): RectConfig => {
    const rect = {
      x: box.x1,
      y: box.y1,
      width: box.x2 - box.x1,
      height: box.y2 - box.y1,
    };

    // * Mapping to the page size in page viewer
    return {
      x: rect.x * ratio.width,
      y: rect.y * ratio.height,
      width: rect.width * ratio.width,
      height: rect.height * ratio.height,
    };
  },

  polygon: ({ polygon }: OCRPositionPolygon, ratio: RectSize): RectConfig => ({
    // * Mapping to the page size in page viewer
    points: polygon.map((value, index) => value * (index % 2 === 0 ? ratio.width : ratio.height)),
    closed: true,
  }),
};

/**
 * Get OCRShape style.
 *
 * @param {Pick<OCRShape, 'active' | 'status'>} shape OCR Shape.
 *
 * @returns {RectConfig} OCRShape style.
 */
export function getOCRShapeStyle({ active, status }: Pick<OCRShape, 'active' | 'status'>): ShapeConfig {
  const base: ShapeConfig = {
    strokeWidth: active ? 2 : 1,
  };

  if (status === 'error') {
    return {
      ...base,
      fill: active ? `rgba(154, 19, 19, 0.25)` : `rgba(234.8, 207.8, 207.8, 0.25)`,
      stroke: active ? `#9a1313` : `rgb(214.6, 160.6, 160.6)`, // Danger
    };
  }

  if (status === 'warning') {
    return {
      ...base,
      fill: active ? `rgba(247, 190, 86, 0.25)` : `rgba(253.4, 242, 221.2, 0.25)`,
      stroke: active ? `#f7be56` : `rgb(251.8, 229, 187.4)`, // Warning
    };
  }

  return {
    ...base,
    fill: active ? `rgba(54, 102, 119, 0.25)` : `rgba(214.8, 224.4, 227.8, 0.25)`,
    stroke: active ? `#366677` : `rgb(174.6, 193.8, 200.6)`, // Primary
  };
}

/**
 * Get ValueRect style.
 *
 * @returns {RectConfig} ValueRect style.
 */
export function getSelectorRectStyle(): RectConfig {
  return {
    strokeWidth: 1,
    cornerRadius: 1,
    fill: `rgba(214.8, 224.4, 227.8, 0.25)`,
    stroke: `#343a40`, // gray-800
  };
}

/**
 * Get HighlightShape style.
 *
 * @returns {RectConfig} HighlightShape style.
 */
export function getHighlightShapeStyle(): ShapeConfig {
  return {
    strokeWidth: 2,
    fill: `rgba(248,255,0, 0.25)`,
    stroke: `#f8ff00`, // Yellow
  };
}

/**
 * Create OCR Shapes Iterator.
 *
 * @param {CreateOCRShapesOptions} options Options.
 *
 * @returns {OCRShapesIterator} OCR Shapes Iterator.
 */
export function createOCRShapesIterator({ page, pageSize, position, status, active }: CreateOCRShapesOptions): OCRShapesIterator {
  const rotatedPosition = rotateOCRPosition(page, position);
  const ratio: RectSize = {
    width: pageSize.width / position.pageWidth,
    height: pageSize.height / position.pageHeight,
  };

  if (isPosition(rotatedPosition, 'natifAI')) {
    const buildItem = (box: OCRPositionBox): OCRShapeRect => ({
      id: createShapeId.box(box),
      status,
      active,
      rect: createOCRShapeConfig.box(box, ratio),
    });

    return {
      forEach: (handle) => {
        rotatedPosition.boxes.forEach((box) => {
          handle(buildItem(box));
        });
      },

      toArray: () => rotatedPosition.boxes.map(buildItem),
    };
  }

  if (isPosition(rotatedPosition, 'azureAI')) {
    const buildItem = (region: OCRPositionPolygon): OCRShapeLine => ({
      id: createShapeId.polygon(region.polygon),
      status,
      active,
      line: createOCRShapeConfig.polygon(region, ratio),
    });

    return {
      forEach: (handle) => {
        rotatedPosition.regions.forEach((region) => {
          handle(buildItem(region));
        });
      },

      toArray: () => rotatedPosition.regions.map(buildItem),
    };
  }

  throw new ClientError({
    module: 'logic',
    message: 'Invalid OCR position',
    cause: { position },
  });
}
